(ns hai.admin-ui.profile.setup
  "\"Get the most out of Humble\" — the setup steps as a strip of folder tabs
   over a detail panel. Selecting a tab tints it and the panel together so the
   two read as one shape.

   Nothing here records progress: the steps are a standing reference, and the
   only controls that act are the calendar connect buttons and the new-tab
   switch, each on its own step."
  (:require
   [hai.journal2.timeline :as tl]
   [hai.onboarding :as onboarding]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.auth :as auth]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.install :as install]))

(def ^:private globe-icon (icons/mui-icon :outlined :language))
(def ^:private phone-icon (icons/mui-icon :outlined :phone_iphone))
(def ^:private android-icon (icons/mui-icon :outlined :android))
(def ^:private lock-icon (icons/mui-icon :outlined :lock))

;; The calendar step is this page's own rather than one of the shared
;; onboarding steps: those are a checklist home stamps off, and connecting a
;; calendar is done by connecting it, not by ticking a box.
(def ^:private step-keys
  (into [::calendar] (map :key) onboarding/steps))

(def lead-text
  (str "Set it up everywhere you work — computer, browser, phone — so "
       "everything stays in sync and you're not switching between tools to "
       "find what you need"))

(def ^:private video-url
  "https://www.loom.com/share/e0e97ff735694a13be730df398da0753")

(def ^:private detail
  {::calendar
   {:title   "Connect your calendar"
    :minutes "1 minute"
    :blurb   "Connect Google or Outlook calendar and today's meetings show up on your home page."
    :bullets ["Every calendar you connect, in one place"
              "No switching between accounts"
              "See where your time actually goes"]
    :footer  [:<> [:strong "Private only to you."]
              " Humble reads your events, never edits or shares them."]}

   :onboarding.step/browser
   {:title    "Install Browser Extension"
    :minutes  "1 minute"
    :blurb    "Add the Humble browser extension, then click it on any page to save a note."
    :bullets  ["Timestamped and tagged automatically"
               "Search for it later, however you remember it"
               "Close the tab with confidence"]
    :cta-link {:label "Add to Chrome" :href install/chrome-store-url-from-home}
    :footer   "Works with Chrome, Edge, Brave, Arc, Dia and Opera."}

   :onboarding.step/homepage
   {:title    "Enable Browser New Tab"
    :minutes  "1 minute"
    :bullets  ["Always there — no need to remember to open it"
               "Shows your tasks, notes and calendar on every new tab"
               "Search bar still works as normal"]
    :footer   "Works in Chrome, Arc, Ecosia and most other Chromium browsers, but not Edge, Safari or Firefox."}

   :onboarding.step/mobile
   {:title    "Add Humble to Mobile"
    :minutes  "2 minutes"
    :blurb    (str "Humble opens from your home screen like any other app — no app store "
                   "needed. Open Humble in your phone's browser, then use \"Add to home screen\".")
    :footer   "No app store needed — works on iPhone and Android."}})

;; Each step is a run of hiccup, not a sentence: the thing to tap carries the
;; emphasis, and the address is set in mono so it reads as something to type.
(def ^:private phone-steps
  {:iphone
   [["Open " [:strong "Safari"] " and go to " [:span.mono "platform.thehumbleai.com"] ", then sign in."]
    ["Tap the " [:strong "share button"] " at the bottom — the box with an arrow pointing up."]
    ["Scroll down and tap " [:strong "Add to Home Screen"] "."]
    ["Tap " [:strong "Add"] " in the top right. Humble appears on your home screen."]]
   :android
   [["Open " [:strong "Chrome"] " and go to " [:span.mono "platform.thehumbleai.com"] ", then sign in."]
    ["Tap the " [:strong "three-dot menu"] " in the top right corner."]
    ["Tap " [:strong "Add to home screen"] ", then tap " [:strong "Add"] ". Humble appears on your home screen."]]})

;; ---------------------------------------------------------------------------
;; Tab strip
;; ---------------------------------------------------------------------------

;; The strip and its panel are one block, not two: as separate children they
;; would each take the section's flex gap and open a band between them.
(o/defstyled tabs-and-panel :div)

;; Tabs overlap by their left margin and clip only their bottom edge, so a
;; selected tab can bleed down into the panel and lose the seam between them.
(o/defstyled tab-strip :div
  :flex
  {:position "relative" :z-index "1" :margin-bottom "-1px"})

(o/defstyled tab :button
  :flex :flex-col
  {:flex "1" :min-width "0"
   :text-align "left"
   :cursor "pointer"
   :font-family "inherit"
   :border "none"
   :border-radius "16px 16px 0 0"
   :padding "13px 20px 12px"
   :gap "8px"
   :margin-top "7px"
   :clip-path "inset(-24px -24px 0 -24px)"
   :box-shadow "6px 0 10px -4px rgba(26,21,48,0.16)"
   :transition "background 180ms ease, margin 180ms ease"}
  ["&:not(:first-child)" {:margin-left "-18px" :padding-left "36px"}]
  ;; The strip's outer edges are the section's edges, so only the seams between
  ;; tabs get a shadow — the first keeps none on its left, the last none on its
  ;; right.
  ["&:last-child" {:box-shadow "none"}]
  [:&.selected
   {:margin-top "0" :margin-bottom "-4px"
    :z-index "20"
    :box-shadow "-6px 0 10px -4px rgba(26,21,48,0.16), 6px 0 10px -4px rgba(26,21,48,0.16)"}]
  ["&.selected:first-child" {:box-shadow "6px 0 10px -4px rgba(26,21,48,0.16)"}]
  ["&.selected:last-child" {:box-shadow "-6px 0 10px -4px rgba(26,21,48,0.16)"}]
  [:.tab-num
   {:font-family "monospace"
    :font-size "12px" :font-weight "700"}]
  [:.tab-title
   {:font-size "14.5px" :font-weight "600" :line-height "1.3"
    :color t/--text-secondary}]

  [:&.step-calendar
   {:background t/--setup-calendar-tab}
   [:.tab-num {:color t/--setup-calendar}]]
  [:&.step-calendar.selected {:background t/--setup-calendar-tint}]
  [:&.step-browser
   {:background t/--setup-browser-tab}
   [:.tab-num {:color t/--setup-browser}]]
  [:&.step-browser.selected {:background t/--setup-browser-tint}]
  [:&.step-newtab
   {:background t/--setup-newtab-tab}
   [:.tab-num {:color t/--setup-newtab}]]
  [:&.step-newtab.selected {:background t/--setup-newtab-tint}]
  [:&.step-phone
   {:background t/--setup-phone-tab}
   [:.tab-num {:color t/--setup-phone}]]
  [:&.step-phone.selected {:background t/--setup-phone-tint}])

;; ---------------------------------------------------------------------------
;; Detail panel
;; ---------------------------------------------------------------------------

(o/defstyled panel :div
  {:border-radius "0 0 16px 16px"
   :transition "background 180ms ease"}
  [:.panel-body {:padding "20px 28px 24px"}]
  [:.panel-eyebrow
   {:font-size "13px" :font-weight "600"
    :letter-spacing "0.06em" :text-transform "uppercase"
    :color t/--colour-dim}]
  [:h3 {:margin "10px 0 0" :font-size "21px" :color t/--colour-ink}]
  [:.panel-blurb
   {:color t/--text-secondary :margin "10px 0 0" :max-width "620px"}]
  [:.panel-bullets
   :flex :flex-col
   {:margin "12px 0 0" :padding "0"
    :list-style "none"
    :gap "8px" :max-width "620px"
    :color t/--text-secondary :font-size "15px"}
   [:li :flex {:gap "10px" :align-items "baseline"}]
   [:.dash {:color t/--colour-dim}]]
  ;; Footer band: a deeper cut of the step's own tint, so it reads as the floor
  ;; of the panel rather than a separate strip.
  [:.panel-footer
   :flex :items-center
   {:gap "10px"
    :padding "13px 28px"
    :border-top "1px solid" :border-top-color t/--colour-line
    :font-size "14px" :color t/--text-secondary
    :border-radius "0 0 16px 16px"
    :transition "background 180ms ease"}
   [:svg {:width "16px" :height "16px" :flex "0 0 auto"}]
   [:strong {:font-weight "600" :color t/--colour-ink}]]

  [:&.step-calendar
   {:background t/--setup-calendar-tint}
   [:.panel-footer {:background t/--setup-calendar-foot}]]
  [:&.step-browser
   {:background t/--setup-browser-tint}
   [:.panel-footer {:background t/--setup-browser-foot}]]
  [:&.step-newtab
   {:background t/--setup-newtab-tint}
   [:.panel-footer {:background t/--setup-newtab-foot}]]
  [:&.step-phone
   {:background t/--setup-phone-tint}
   [:.panel-footer {:background t/--setup-phone-foot}]])

(o/defstyled cta :a
  :inline-flex :items-center
  {:width "fit-content"
   :gap "8px"
   :margin-top "20px"
   :padding "12px 22px"
   :border-radius "12px"
   :background t/--colour-purple
   :color t/--colour-white
   :font-size "15px" :font-weight "600"
   :text-decoration "none"
   :transition "background 180ms ease"}
  [:&:hover {:background t/--colour-ink :color t/--colour-white}])

;; The calendar step offers two providers, so it can't lean on the single
;; filled CTA the browser step uses: Google carries the fill, Outlook sits
;; beside it as the outlined alternative.
(o/defstyled connect-row :div
  :flex :items-center
  {:gap "12px" :margin-top "20px" :flex-wrap "wrap"})

(o/defstyled connect-button :button
  :inline-flex :items-center
  {:gap "8px"
   :padding "12px 22px"
   :border "1px solid" :border-color t/--colour-ink
   :border-radius "12px"
   :background "transparent"
   :color t/--colour-ink
   :font-family "inherit"
   :font-size "15px" :font-weight "600"
   :cursor "pointer"
   :transition "background 180ms ease, border-color 180ms ease, color 180ms ease"}
  [:&:hover {:background t/--colour-ink :color t/--colour-white}]
  [:&.primary
   {:background t/--colour-purple
    :border-color t/--colour-purple
    :color t/--colour-white}
   [:&:hover {:background t/--colour-ink :border-color t/--colour-ink}]])

(o/defstyled switch-row :div
  :flex
  {:gap "14px" :margin-top "16px" :max-width "620px"
   :align-items "flex-start"}
  [:.switch-name {:font-size "15px" :font-weight "600" :color t/--text-secondary}]
  [:.switch-note {:font-size "14px" :color t/--text-secondary :margin "4px 0 0"}])

(o/defstyled callout :div
  {:margin-top "16px" :max-width "620px"
   :background t/--colour-pink-20
   :border "1px solid" :border-color t/--colour-pink
   :border-radius "12px"
   :padding "14px 16px"
   :font-size "14px" :color t/--text-secondary}
  [:strong {:color t/--colour-ink}])

(o/defstyled platform-picker :div
  :inline-flex
  {:gap "2px" :margin-top "18px"
   :background t/--colour-chalk
   :border "1px solid" :border-color t/--colour-rock
   :border-radius "999px"
   :padding "3px"}
  [:button
   :inline-flex :items-center
   {:gap "7px"
    :font-family "inherit" :font-size "14px" :font-weight "600"
    :padding "8px 18px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :color t/--text-secondary
    :cursor "pointer"
    :transition "background 180ms ease"}
   [:&.selected
    {:background t/--colour-white
     :color t/--colour-ink
     :box-shadow "0 1px 2px rgba(26,21,48,0.12)"}]
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]])

(o/defstyled phone-instructions :ol
  :flex :flex-col
  {:margin "14px 0 0" :padding "0"
   :list-style "none"
   :gap "10px" :max-width "620px"
   :color t/--text-secondary :font-size "15px"}
  [:li :flex {:gap "10px" :align-items "baseline"}]
  [:.dash {:color t/--colour-dim}]
  [:strong {:font-weight "600" :color t/--colour-ink}]
  [:.mono {:font-family "monospace" :font-size "13.5px"}])

;; The quiet aside a panel hangs under its main content — a caveat, or a way
;; out for people the step doesn't quite fit.
(o/defstyled panel-note :p
  {:font-size "14px" :color t/--text-secondary :margin "16px 0 0"}
  [:strong {:font-weight "600" :color t/--colour-ink}]
  [:a
   {:color t/--colour-purple
    :text-decoration "underline" :text-underline-offset "2px"}
   [:&:hover {:color t/--colour-ink}]])

;; ---------------------------------------------------------------------------
;; Panels
;; ---------------------------------------------------------------------------

(defn- bullet-list
  [items]
  [:ul.panel-bullets
   (for [item items]
     ^{:key item} [:li [:span.dash "–"] [:span item]])])

(defn- calendar-panel
  "Both providers connect through the timeline's own flow, so an account that
   is already linked is enabled straight away and only a new one is sent to
   the provider — which is what the modal below is here to offer."
  [items]
  [:<>
   [bullet-list items]
   [connect-row
    [connect-button
     {:type     "button"
      :on-click #(>e [:timeline/add-integration-by-type
                      :timeline-integration.type/outlook-calendar])}
     "Connect Outlook Calendar"]
    [connect-button
     {:type     "button"
      :class    "primary"
      :on-click #(>e [:timeline/add-integration-by-type
                      :timeline-integration.type/google-calendar])}
     "Connect Google Calendar"]]
   [panel-note
    "Use Apple Calendar? Sync it to a Google or Outlook account first, then "
    "connect that account here."]
   [tl/not-connected-modal]])

(def ^:private newtab-label-id "profile-setup-newtab")

(defn- newtab-panel
  [items]
  [:<>
   [switch-row
    [c/Switch {:checked           (boolean (:user-config.newtab/override? @(<s :auth/identity)))
               :aria-labelledby   newtab-label-id
               :on-checked-change #(>e [::auth/set-newtab-override %])}]
    [:div
     [:div.switch-name {:id newtab-label-id} "Open Humble on new tabs"]
     [:p.switch-note "Every new tab shows Humble instead of a blank page."]]]
   [callout
    [:strong "Chrome will ask about this change. "]
    "The first new tab shows a banner saying \"Humble changed your new tab page\". Choose "
    [:strong "Keep it"]
    " — choosing \"Change it back\" will undo this step."]
   [bullet-list items]])

(defn- phone-panel
  [!platform]
  (let [platform @!platform]
    [:<>
     [platform-picker
      (for [[key label icon] [[:iphone "iPhone" phone-icon]
                              [:android "Android" android-icon]]]
        ^{:key key}
        [:button {:type         "button"
                  :class        (when (= key platform) "selected")
                  :aria-pressed (= key platform)
                  :on-click     #(reset! !platform key)}
         [icon]
         label])]
     (when (= :iphone platform)
       [panel-note
        [:strong "Use Safari for these steps"]
        " — on iPhone, Chrome can't add Humble to your home screen."])
     [phone-instructions
      (for [[index step] (map-indexed vector (phone-steps platform))]
        [:li {:key index}
         [:span.dash "–"]
         (into [:span] step)])]
     [panel-note
      "Prefer to watch? "
      [:a {:href video-url :target "_blank" :rel "noopener"} "Watch the two-minute video"]
      " or open the "
      [:a {:href install/mobile-setup-url :target "_blank" :rel "noopener"} "full setup guide"]
      " on your phone."]]))

;; ---------------------------------------------------------------------------
;; Section
;; ---------------------------------------------------------------------------

(defn- step-class
  [step-key]
  (case step-key
    ::calendar                "step-calendar"
    :onboarding.step/browser  "step-browser"
    :onboarding.step/homepage "step-newtab"
    :onboarding.step/mobile   "step-phone"))

(defn- footer-icon
  [step-key]
  (case step-key
    ::calendar              [lock-icon]
    :onboarding.step/mobile [phone-icon]
    [globe-icon]))

(defn steps-view []
  (r/with-let [!selected (r/atom (first step-keys))
               !platform (r/atom :iphone)]
    (let [selected @!selected
          total    (count step-keys)
          position (inc (.indexOf step-keys selected))
          {:keys [title minutes blurb bullets cta-link footer]}
          (detail selected)]
      [tabs-and-panel
       [tab-strip
        (doall
         (for [[index step-key] (map-indexed vector step-keys)]
           ^{:key step-key}
           [tab {:type     "button"
                 :role     "tab"
                 :aria-selected (= step-key selected)
                 :class    (cond-> [(step-class step-key)]
                             (= step-key selected) (conj "selected"))
                 :style    {:z-index (if (= step-key selected) 20 (- 10 index))}
                 :on-click #(reset! !selected step-key)}
            [:span.tab-num (str "0" (inc index))]
            [:span.tab-title (:title (detail step-key))]]))]
       [panel {:class (step-class selected)}
        [:div.panel-body
         [:div.panel-eyebrow (str "Step " position " of " total " · takes about " minutes)]
         [:h3 title]
         (when blurb [:p.panel-blurb blurb])
         (case selected
           ::calendar                [calendar-panel bullets]
           :onboarding.step/homepage [newtab-panel bullets]
           :onboarding.step/mobile   [phone-panel !platform]
           [:<>
            [bullet-list bullets]
            (when-let [{:keys [label href]} cta-link]
              [cta {:href href :target "_blank" :rel "noopener"} label])])]
        [:div.panel-footer
         (footer-icon selected)
         [:span footer]]]])))
